import React, { useEffect, useRef, useState } from 'react';
import logger from '../../utils/logger';
import './OnboardingTour.css';

const STEPS = [
  {
    title: 'Добро пожаловать в Энилекс',
    body: [
      'Коротко покажем, как здесь всё устроено — четыре экрана, меньше минуты.',
      'Работа строится так: проект объединяет кабинеты маркетплейсов, а внутри проекта живут задачи, аналитика и автоматизации.'
    ]
  },
  {
    title: 'Шаг 1. Создайте проект',
    body: [
      'Проект — рабочее пространство компании. В него складываются кабинеты, задачи и доступы сотрудников.',
      'Сотрудник, добавленный в проект, автоматически получает доступ ко всем кабинетам этого проекта.'
    ],
    action: { label: 'Создать проект', target: 'projects' }
  },
  {
    title: 'Шаг 2. Подключите кабинет',
    body: [
      'Кабинет — это подключение к OZON или Wildberries по API-ключу. Ключи хранятся в зашифрованном виде.',
      'Для OZON нужен Seller ID и API-ключ; для рекламы дополнительно Performance Client ID и Client Secret.',
      'После подключения платформа сама подтянет товары, заказы и статистику.'
    ],
    action: { label: 'Подключить кабинет', target: 'cabinets' }
  },
  {
    title: 'Шаг 3. Тариф и лимиты',
    body: [
      'Тариф назначается на каждый кабинет отдельно: free, standard или full.',
      'Компания получает функции по лучшему из тарифов своих кабинетов.',
      'На free доступны базовая аналитика и задачи; платные тарифы открывают рекламу, автоматизации и выгрузки.'
    ],
    action: { label: 'Открыть тарифы', target: 'billing' }
  },
  {
    title: 'С чего начать',
    body: [
      'Создайте проект и подключите первый кабинет — остальное появится само.',
      'Первая синхронизация занимает несколько минут: товары подтянутся сразу, статистика — по мере загрузки истории.',
      'Тур можно пройти заново в Настройках → Аккаунт.'
    ]
  }
];

function OnboardingTour({ request, setUser, showStatus, onNavigate, onClose }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  const finish = async (navigateTo) => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await request('/api/v1/auth/me/onboarding', {
        method: 'PATCH',
        body: JSON.stringify({ completed: true })
      });
      if (updated && setUser) setUser(updated);
    } catch (error) {
      logger.error('Failed to mark onboarding complete:', error);
      if (showStatus) showStatus('error', 'Не удалось сохранить отметку о туре');
    } finally {
      setBusy(false);
      if (navigateTo && onNavigate) onNavigate(navigateTo);
      if (onClose) onClose();
    }
  };

  // Esc закрывает тур так же, как «Пропустить» (отметка сохраняется).
  const finishRef = useRef(finish);
  finishRef.current = finish;
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') finishRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="onboarding__backdrop" role="dialog" aria-modal="true" aria-label="Вводный тур">
      <div className="onboarding__card">
        <div className="onboarding__progress">
          {STEPS.map((item, index) => (
            <span
              key={item.title}
              className={index <= stepIndex ? 'onboarding__dot onboarding__dot--done' : 'onboarding__dot'}
            />
          ))}
        </div>

        <h2 className="onboarding__title">{step.title}</h2>
        {step.body.map((paragraph) => (
          <p key={paragraph} className="onboarding__text">{paragraph}</p>
        ))}

        {step.action && (
          <button
            type="button"
            className="onboarding__jump"
            disabled={busy}
            onClick={() => finish(step.action.target)}
          >
            {step.action.label} →
          </button>
        )}

        <div className="onboarding__footer">
          <button type="button" className="onboarding__skip" disabled={busy} onClick={() => finish(null)}>
            {isLast ? 'Закрыть' : 'Пропустить'}
          </button>

          <div className="onboarding__nav">
            {stepIndex > 0 && (
              <button type="button" className="onboarding__back" disabled={busy} onClick={() => setStepIndex(stepIndex - 1)}>
                Назад
              </button>
            )}
            <button
              type="button"
              className="onboarding__next"
              disabled={busy}
              onClick={() => (isLast ? finish(null) : setStepIndex(stepIndex + 1))}
            >
              {isLast ? 'Начать работу' : 'Далее'}
            </button>
          </div>
        </div>

        <div className="onboarding__counter">{stepIndex + 1} из {STEPS.length}</div>
      </div>
    </div>
  );
}

export default OnboardingTour;
